// Metro-level DPA metadata powering /grants/city/:city programmatic SEO pages.
// Bet 4 of the organic-growth roadmap. Metro seed = shared with metrosMortgageRates.ts.
// Real dollar caps and program terms are intentionally NOT quoted here — those come
// from the live ZIP-based Grant Finder to keep content honest and evergreen.

import { METRO_RATES, type MetroRateInfo } from "./metrosMortgageRates";
import { getStateBySlug, type StateGrantInfo } from "./statesGrants";

export interface MetroGrantInfo {
  slug: string;
  city: string;
  metro: string;
  stateName: string;
  stateAbbr: string;
  stateSlug: string;
  medianHomePrice: number;
  /** 1–3 named local/county DPA programs (public info). */
  cityDpaPrograms: string[];
  /** Short local-availability read. */
  marketNote: string;
  /** Composed parent-state HFA info (for stacking layer copy). */
  state?: StateGrantInfo;
}

// Hand-curated named programs for the highest-search metros. Any metro not in this
// map falls back to a generic "county-level DPA typical in {state}" line — still
// unique-per-page because it names the state HFA and metro area.
const NAMED_METRO_PROGRAMS: Record<string, string[]> = {
  "new-york-ny": ["NYC HomeFirst Down Payment Assistance", "HomeFirst forgivable second mortgage", "SONYMA Achieving the Dream"],
  "los-angeles-ca": ["LA County Home Ownership Program (HOP)", "LAHD Low Income Purchase Assistance Program (LIPA)", "CalHFA MyHome Assistance"],
  "chicago-il": ["Chicago Home Buyer Assistance (CHBA)", "IHDA Access Forgivable / Deferred / Repayable", "Cook County HOME Buyer Program"],
  "dallas-tx": ["Dallas Homebuyer Assistance Program (DHAP)", "TDHCA My First Texas Home", "TSAHC Home Sweet Texas Home"],
  "houston-tx": ["City of Houston Homebuyer Assistance (HAP)", "Harris County DPA", "TSAHC Homes for Texas Heroes"],
  "washington-dc": ["DC Home Purchase Assistance Program (HPAP)", "DCHFA DC Open Doors", "Employer-Assisted Housing (EAHP)"],
  "miami-fl": ["Miami-Dade Infill Housing Initiative", "City of Miami DPA", "Florida Housing Hometown Heroes"],
  "philadelphia-pa": ["Philly First Home", "PHFA Keystone Advantage Assistance", "Philly Turn the Key"],
  "atlanta-ga": ["Atlanta Affordable Homeownership Program (AAHOP)", "Georgia Dream Standard", "Invest Atlanta Homeownership Assistance"],
  "phoenix-az": ["Phoenix Open Doors DPA", "Home in Five Advantage (Maricopa County)", "Home Plus (Arizona IDA)"],
  "boston-ma": ["ONE+Boston Mortgage Program", "MassHousing DPA", "Boston Home Center Homebuyer Program"],
  "san-francisco-ca": ["SF DALP (Downpayment Assistance Loan Program)", "SF Teacher Next Door", "CalHFA MyHome + Forgivable Equity Builder"],
  "seattle-wa": ["House Key Opportunity (WSHFC)", "ARCH East King County DPA", "Seattle Office of Housing DPA"],
  "denver-co": ["metroDPA (Colorado Housing Assistance Corporation)", "CHFA DPA Grant", "Denver Housing Authority DPA"],
  "austin-tx": ["Austin Housing Finance Corp DPA", "TSAHC Home Sweet Texas Home", "TDHCA My First Texas Home"],
  "san-diego-ca": ["San Diego Housing Commission First-Time Homebuyer Program", "CalHFA MyHome", "GSFA Platinum"],
  "san-jose-ca": ["San Jose Empower Homebuyers SCC", "CalHFA MyHome", "Housing Trust Silicon Valley DPA"],
  "sacramento-ca": ["CalHFA MyHome + Forgivable Equity Builder", "Sacramento Housing and Redevelopment Agency DPA", "GSFA Platinum"],
  "portland-or": ["Portland Housing Bureau Homebuyer Assistance", "OHCS DPA", "Proud Ground shared-equity program"],
  "las-vegas-nv": ["Home Is Possible (Nevada Housing Division)", "Home At Last (NRHA)", "Clark County DPA"],
  "nashville-tn": ["THDA Great Choice + DPA", "Metro Nashville Barnes Fund", "Metro Action Commission DPA"],
  "charlotte-nc": ["House Charlotte Program", "NC Home Advantage + DPA", "NC 1st Home Advantage $15,000 DPA"],
  "orlando-fl": ["City of Orlando DPA", "Orange County SHIP", "Florida Housing Hometown Heroes"],
  "detroit-mi": ["Detroit 0% Interest Home Repair Loan / MSHDA MI Home Loan", "MSHDA MI 10K DPA", "Detroit Homeowners Property Exemption bridge programs"],
  "minneapolis-mn": ["Minneapolis Homeownership Works (HOW)", "Minnesota Housing Start Up + Deferred Payment Loan", "City Living Grant"],
  "san-antonio-tx": ["San Antonio Homeownership Incentive Program (HIP)", "TSAHC Homes for Texas Heroes", "TDHCA My First Texas Home"],
  "baltimore-md": ["Baltimore Buying Into Baltimore", "Maryland Mortgage Program + DPA", "Live Near Your Work"],
  "salt-lake-city-ut": ["Utah Housing FirstHome + DPA", "Salt Lake County HOME DPA", "Own in Ogden / SLC Community Development DPA"],
  "raleigh-nc": ["Raleigh Homebuyer Assistance", "NC Home Advantage + DPA", "NC 1st Home Advantage $15,000 DPA"],
  "tampa-fl": ["City of Tampa DPA", "Hillsborough County DPA", "Florida Housing Hometown Heroes"],
  "jacksonville-fl": ["Jacksonville Head Start to Home Ownership (H2H)", "Duval County SHIP", "Florida Housing Hometown Heroes"],
  "columbus-oh": ["Columbus American Dream Down Payment Initiative", "OHFA YourChoice! DPA", "Franklin County DPA"],
  "indianapolis-in": ["Indy Neighborhood Housing Partnership DPA", "IHCDA Next Home + DPA", "Marion County DPA"],
  "kansas-city-mo": ["Kansas City Homebuyer Assistance", "MHDC First Place Loan", "Housing Options Made Easy (HOMES) DPA"],
  "milwaukee-wi": ["Milwaukee Homebuyer Assistance", "WHEDA Easy Close DPA", "Take Root Milwaukee"],
  "louisville-ky": ["Louisville Metro Down Payment Assistance", "KHC Regular / Affordable DAP", "Louisville CDA Homebuyer Program"],
  "memphis-tn": ["Memphis Down Payment Assistance", "THDA Great Choice + DPA", "Shelby County DPA"],
  "oklahoma-city-ok": ["OKC Homebuyer Assistance", "OHFA Homebuyer Dream", "REI Gift100"],
  "richmond-va": ["Richmond Affordable Home Ownership (RAHOP)", "Virginia Housing DPA Grant", "Richmond MOU Employer-Assisted Housing"],
  "new-orleans-la": ["NOLA Soft Second Mortgage Program", "LHC Market Rate GNMA + DPA", "Delta 100 (NOLA rural)"],
  "buffalo-ny": ["Buffalo Home Ownership Zone", "SONYMA Achieving the Dream + DPA", "Erie County HOME DPA"],
  "cleveland-oh": ["Cleveland Home Enhancement Loan Program (bridge)", "OHFA Your Choice! DPA", "Cuyahoga County Down Payment Assistance"],
  "pittsburgh-pa": ["Pittsburgh Home Ownership Program (P-HOP)", "PHFA Keystone Advantage Assistance", "URA First Front Door"],
  "cincinnati-oh": ["Cincinnati American Dream Downpayment Initiative (ADDI)", "OHFA Your Choice! DPA", "Hamilton County DPA"],
  "st-louis-mo": ["St. Louis DPA Program", "MHDC First Place Loan", "Beyond Housing DPA"],
  "hartford-ct": ["Hartford HouseHartford Homebuyer Assistance", "CHFA Downpayment Assistance Program", "HOMEConnecticut"],
  "providence-ri": ["RIHousing FirstGenHomeRI DPA", "Providence Home Repair Loan (bridge)", "RIHousing Extra Assistance"],
  "virginia-beach-va": ["Virginia Beach HOME DPA", "Virginia Housing DPA Grant", "Hampton Roads REALTORS DPA"],
  "honolulu-hi": ["Hawaii DHHL Native Hawaiian Homeownership", "HHFDC Mortgage Credit Certificate", "City & County of Honolulu DPA"],
  "birmingham-al": ["Birmingham Homebuyer Assistance", "AHFA Step Up + Down Payment Assistance", "Jefferson County HOME DPA"],
  "boise-id": ["Ada County Housing Authority DPA", "IHFA First Loan + DPA", "Boise City DPA"],
  "colorado-springs-co": ["El Paso County Turnkey Plus DPA", "CHFA DPA Grant", "Pikes Peak REALTORS DPA"],
  "boulder-co": ["Boulder Homeownership Program", "CHFA DPA Grant", "Thistle Community Housing shared-equity"],
  "charleston-sc": ["Charleston Homebuyer Assistance", "SC Housing Homebuyer Program + DPA", "Palmetto Home Advantage"],
  "columbia-sc": ["Columbia Homeownership Program", "SC Housing Homebuyer Program + DPA", "Palmetto Home Advantage"],
};

function fallbackPrograms(state: StateGrantInfo | undefined, city: string): string[] {
  if (!state) return [`${city} county-level DPA (see Stack)`, "Federal FHA / VA / USDA loans"];
  return [
    `${state.hfaShort} state DPA (${state.name})`,
    `${city} area county-level DPA`,
    `Federal FHA / VA / USDA loans`,
  ];
}

function buildMetroGrantInfo(m: MetroRateInfo): MetroGrantInfo {
  const state = getStateBySlug(m.stateSlug);
  const programs = NAMED_METRO_PROGRAMS[m.slug] ?? fallbackPrograms(state, m.city);
  const hfaShort = state?.hfaShort ?? `${m.stateName} HFA`;
  const marketNote = NAMED_METRO_PROGRAMS[m.slug]
    ? `${m.city} has named city or county DPA programs that stack on top of ${hfaShort} state assistance — worth checking every layer before writing an offer.`
    : `${m.city} buyers typically stack ${hfaShort} state DPA with any local county programs and federal FHA/VA/USDA loans. County-level programs vary by ZIP within the ${m.metro} area.`;
  return {
    slug: m.slug,
    city: m.city,
    metro: m.metro,
    stateName: m.stateName,
    stateAbbr: m.stateAbbr,
    stateSlug: m.stateSlug,
    medianHomePrice: m.medianHomePrice,
    cityDpaPrograms: programs,
    marketNote,
    state,
  };
}

export const METRO_GRANTS: MetroGrantInfo[] = METRO_RATES.map(buildMetroGrantInfo);

export function getMetroGrantBySlug(slug: string | undefined): MetroGrantInfo | undefined {
  if (!slug) return undefined;
  return METRO_GRANTS.find((m) => m.slug === slug.toLowerCase());
}

export function getSiblingMetroGrants(slug: string, count = 3): MetroGrantInfo[] {
  const i = METRO_GRANTS.findIndex((m) => m.slug === slug);
  if (i === -1) return [];
  const out: MetroGrantInfo[] = [];
  for (let k = 1; k <= count; k++) out.push(METRO_GRANTS[(i + k) % METRO_GRANTS.length]);
  return out;
}

export function getMetroGrantsByState(stateSlug: string): MetroGrantInfo[] {
  return METRO_GRANTS.filter((m) => m.stateSlug === stateSlug);
}